import { Component, Input, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BulkSelectEmployeeDialogComponent, DeprecatedConfirmationDialogComponent } from '@app/components';
import { FileHelperService, NotifyService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { ShowView } from '@common/views';
import { Employee } from '@models/employee/employee.model';
import { TrainingProgram } from '@models/training/training-program.model';
import { EnrolledEmployeesTable } from '@training/services';

@Component({
    templateUrl: './program-show.template.html',
    styleUrls: ['./program-show.style.scss'],
})
export class ProgramShowView extends ShowView {
    @ViewChild('confirmDeleteDialog', { static: true }) confirmDeleteDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('selectEmployees', { static: true }) selectEmployees: BulkSelectEmployeeDialogComponent;
    @Input() label;
    program: TrainingProgram;
    table: EnrolledEmployeesTable;
    isLoading = false;
    allEnrolled: Employee[];
    originalEnrolledIds: number[];

    constructor(
        public auth: AuthService,
        public fileHelper: FileHelperService,
        private route: ActivatedRoute,
        private router: Router,
        protected notify: NotifyService
    ) {
        super();
        this.program = this.route.snapshot.data.models.get('trainingProgram');
        this.table = new EnrolledEmployeesTable(this.auth, this.program);
    }

    ngOnInit(): void {
        this.initializeEmployees();
    }

    onEdit(): void {
        this.router.navigate(['/training', 'programs', this.program.id, 'edit']);
    }

    onCancel(): void {}

    get deleteMessage(): string {
        if (this.program && this.program.enrolledCount === 1) {
            return 'training.deleteTrainingProgramConfirmation.singular';
        }

        if (this.program && this.program.enrolledCount > 1) {
            return 'training.deleteTrainingProgramConfirmation.plural';
        }

        return 'training.deleteTrainingProgramConfirmation.default';
    }

    async onDelete(): Promise<void> {
        const onDelete = await this.confirmDeleteDialog.show();

        if (!onDelete) {
            return;
        }

        this.isLoading = true;
        this.program
            .param('company', this.auth.company.id)
            .delete()
            .then(() => {
                this.isLoading = false;
                this.notify.success('training.deleteTrainingSuccessMessage');
                this.router.navigate(['/training', 'programs']);
            })
            .catch((err) => {
                this.notify.error(err.message || 'training.deleteTrainingErrorMessage');
                this.isLoading = false;
            });
    }

    onSave(): void {}

    onAssignClick(): void {
        this.selectEmployees.show<Employee[]>(this.allEnrolled).then((selected: Employee[]) => {
            if (selected === null) {
                return;
            }
            this.allEnrolled = selected;
            this.attachEmployeesToProgram();
        });
    }
    protected initializeEmployees(): void {
        Employee.param('company', this.auth.company.id)
            .where('trainingProgramId', this.program.id)
            .all()
            .then(([enrolled, _]: [Employee[], object]) => {
                this.allEnrolled = enrolled;
                this.originalEnrolledIds = enrolled.map((e: Employee) => e.id);
            });
    }

    protected async attachEmployeesToProgram(): Promise<void> {
        const selectedIds = this.allEnrolled.map((e: Employee) => e.id);
        this.program.cleanUp();
        this.program.attach(
            'employees',
            selectedIds.filter((selectedId: number) => {
                return this.originalEnrolledIds.indexOf(selectedId) < 0;
            })
        );

        this.program.detach(
            'employees',
            this.originalEnrolledIds.filter((enrolledId: number) => {
                return selectedIds.indexOf(enrolledId) < 0;
            })
        );

        await this.program.save();
        this.table.reload();
    }
}
